import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useState, useRef, useEffect } from "react";
import { X, Upload, Hotel, Star, Image as ImageIcon } from "lucide-react";
import { propertyTypes } from "@/constants/facities";
import RoomMixModalAdmin from "@/modules/underwriting/RoomMixModalAdmin";
import { supabase } from "@/lib/supabaseClient";
import { RoomMixItem } from "@/lib/kpis";

const facilities = [
  "parking",
  "restaurant",
  "bar",
  "pool",
  "spa",
  "gym",
  "roomservice",
];

interface DealBasicTabProps {
  formData: any;
  onFormDataChange: (data: any) => void;
  kpis: any;
  onKpisChange: (data: any) => void;
  dealId: string;
}

interface ImageFile {
  id: string;
  url: string;
  file: File;
  isMain?: boolean;
}

export function DealBasicTab({
  formData,
  onFormDataChange,
  kpis,
  onKpisChange,
  dealId,
}: DealBasicTabProps) {
  const [imageFiles, setImageFiles] = useState<ImageFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isRoomMixModalOpen, setIsRoomMixModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  console.log("formData", formData.stars);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;

    const images: ImageFile[] = [];
    const seenUrls = new Set<string>();

    if (formData.photoUrl && !seenUrls.has(formData.photoUrl)) {
      seenUrls.add(formData.photoUrl);
      images.push({
        id: "main-" + Math.random().toString(36).substr(2, 9),
        url: formData.photoUrl,
        file: new File([], "main-image"),
        isMain: true,
      });
    }

    if (formData.photos && Array.isArray(formData.photos)) {
      formData.photos.forEach((url: string) => {
        if (url && !seenUrls.has(url)) {
          seenUrls.add(url);
          images.push({
            id: Math.random().toString(36).substr(2, 9),
            url,
            file: new File([], "existing-image"),
            isMain: false,
          });
        }
      });
    }

    if (images.length > 0) {
      setImageFiles(images);
      initializedRef.current = true;
    }
  }, [formData.photoUrl, formData.photos, dealId]);

  useEffect(() => {
    initializedRef.current = false;
  }, [dealId]);

  const uploadToSupabase = async (file: File): Promise<string> => {
    const fileExt = file.name.split(".").pop();
    const fileName = `${dealId}/${crypto.randomUUID()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from("project-assets")
      .upload(fileName, file);

    if (uploadError) {
      throw uploadError;
    }

    const {
      data: { publicUrl },
    } = supabase.storage.from("project-assets").getPublicUrl(fileName);

    return publicUrl;
  };

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || uploading) return;

    setUploading(true);
    try {
      const newImages: ImageFile[] = [];
      const currentCount = imageFiles.length;

      for (
        let i = 0;
        i < files.length && currentCount + newImages.length < 10;
        i++
      ) {
        const file = files[i];
        if (file.type.startsWith("image/")) {
          const publicUrl = await uploadToSupabase(file);

          newImages.push({
            id: crypto.randomUUID(),
            url: publicUrl,
            file,
            isMain: false,
          });
        }
      }

      if (newImages.length > 0) {
        setImageFiles((prev) => {
          const updatedImages = [...prev, ...newImages];

          // If no main image yet, make the first uploaded one the main
          const hasMain = updatedImages.some((img) => img.isMain);
          if (!hasMain && updatedImages.length > 0) {
            updatedImages[0].isMain = true;
          }

          const allUrls = updatedImages.map((img) => img.url);
          const mainImage = updatedImages.find((img) => img.isMain);

          onFormDataChange({
            ...formData,
            photos: allUrls,
            photoUrl: mainImage?.url || allUrls[0] || "",
          });

          return updatedImages;
        });
      }
    } catch (error) {
      console.error("Error uploading images:", error);
    } finally {
      setUploading(false);
      // Reset file input so same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveImage = (id: string) => {
    const imageToRemove = imageFiles.find((img) => img.id === id);
    const newImages = imageFiles.filter((img) => img.id !== id);

    if (imageToRemove?.isMain && newImages.length > 0) {
      newImages[0] = { ...newImages[0], isMain: true };
    }

    setImageFiles(newImages);

    const urls = newImages.map((img) => img.url);
    const mainImage = newImages.find((img) => img.isMain);

    onFormDataChange({
      ...formData,
      photos: urls,
      photoUrl: mainImage?.url || urls[0] || "",
    });
  };

  const handleSetMainImage = (id: string) => {
    const updatedImages = imageFiles.map((img) => ({
      ...img,
      isMain: img.id === id,
    }));

    setImageFiles(updatedImages);

    const mainImage = updatedImages.find((img) => img.isMain);
    const urls = updatedImages.map((img) => img.url);

    onFormDataChange({
      ...formData,
      photos: urls,
      photoUrl: mainImage?.url || urls[0] || "",
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  useEffect(() => {
    return () => {
      imageFiles.forEach((img) => {
        if (img.url.startsWith("blob:")) {
          URL.revokeObjectURL(img.url);
        }
      });
    };
  }, []);

  const handleSelectImages = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (field: string, value: any) => {
    const updatedFormData = { ...formData, [field]: value };

    if (field === "roomsTotal") {
      updatedFormData.total_rooms = value;
    }

    onFormDataChange(updatedFormData);

    const kpisUpdates: any = {};

    if (field === "name") kpisUpdates.dealName = value;
    if (field === "city") {
      kpisUpdates.location = `${value}, ${formData.country || ""}`.trim();
      kpisUpdates.address = value;
      kpisUpdates.fullAddress = value;
    }
    if (field === "country") {
      kpisUpdates.location = `${formData.city || ""}, ${value}`.trim();
      kpisUpdates.dealLocation = value;
    }
    if (field === "propertyType") kpisUpdates.propertyType = value;
    if (field === "stars") {
      kpisUpdates.stars = value;
    }
    if (field === "purchasePrice") {
      kpisUpdates.purchase = { price: value };
      kpisUpdates.purchasePrice = value;
    }
    if (field === "currency") kpisUpdates.currency = value;
    if (field === "gfaSqm") kpisUpdates.gfaSqm = value;
    if (field === "photos") kpisUpdates.photos = value;
    if (field === "photoUrl") kpisUpdates.photoUrl = value;
    if (field === "facilities") {
      const newAmenities = {
        bar: value.includes("bar"),
        gym: value.includes("gym"),
        spa: value.includes("spa"),
        pool: value.includes("pool"),
        parking: value.includes("parking"),
        meetings: value.includes("conference") || value.includes("meetings"),
        restaurant: value.includes("restaurant"),
        room_service: value.includes("roomservice"),
      };
      kpisUpdates.facilities = value;
      kpisUpdates.normalizedFacilities = value;
      kpisUpdates.property = { facilities: value };
      kpisUpdates.amenities = newAmenities;
    }
    if (field === "fullAddress") {
      kpisUpdates.fullAddress = value;
      kpisUpdates.address = value;
    }

    if (field === "description") {
      kpisUpdates.description = value;
    }

    if (field === "investmentThesis") {
      kpisUpdates.investmentThesis = value;
    }

    if (Object.keys(kpisUpdates).length > 0) {
      onKpisChange({ ...kpis, ...kpisUpdates });
    }
  };
  const totalRoomsFromMix =
    kpis?.roomMix?.reduce(
      (sum: number, item: any) =>
        sum + (Number(item.rooms) || Number(item.count) || 0),
      0,
    ) || 0;

  const getRoomMixSummary = () => {
    if (!kpis?.roomMix || kpis.roomMix.length === 0) return "Not configured";
    const types = kpis.roomMix.map((item: any) => item.name).join(", ");
    return `${totalRoomsFromMix} rooms (${types})`;
  };

  const handleFacilityToggle = (facility: string) => {
    const currentFacilities = formData.facilities || [];
    const newFacilities = currentFacilities.includes(facility)
      ? currentFacilities.filter((f: string) => f !== facility)
      : [...currentFacilities, facility];

    handleInputChange("facilities", newFacilities);
  };

  const handleRoomMixSaved = (
    savedRoomMix: RoomMixItem[],
    totalRooms: number,
  ) => {
    setIsRoomMixModalOpen(false);

    const updatedKpis = {
      ...kpis,
      roomMix: savedRoomMix,
      rooms: { total: totalRooms },
    };
    onKpisChange(updatedKpis);

    onFormDataChange({
      ...formData,
      roomsTotal: totalRooms,
      total_rooms: totalRooms,
    });
  };

  const mainImage = imageFiles.find((img) => img.isMain);
  const otherImages = imageFiles.filter((img) => !img.isMain);

  return (
    <TabsContent value="basic" className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Property Name *</Label>
              <Input
                placeholder="e.g., Boutique Hotel Roma"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>City *</Label>
              <Input
                placeholder="e.g., Rome"
                value={formData.city || ""}
                onChange={(e) => handleInputChange("city", e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Country *</Label>
              <Input
                placeholder="e.g., Italy"
                value={formData.country}
                onChange={(e) => handleInputChange("country", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Property Type *</Label>
              <Select
                value={formData.propertyType}
                onValueChange={(value) =>
                  handleInputChange("propertyType", value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {propertyTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Star Rating</Label>
              <Select
                value={String(formData.stars)}
                onValueChange={(value) =>
                  handleInputChange("stars", Number(value))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n} Stars
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Property Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select
                value={formData.currency}
                onValueChange={(value) => handleInputChange("currency", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                  <SelectItem value="PHP">PHP</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>GFA (sqm)</Label>
              <Input
                type="number"
                placeholder="e.g., 2500"
                value={formData.gfaSqm || ""}
                onChange={(e) =>
                  handleInputChange("gfaSqm", Number(e.target.value))
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Room Mix *</Label>
              <div
                onClick={() => setIsRoomMixModalOpen(true)}
                className="flex items-center justify-between px-3 py-2 bg-white border border-slate-200 rounded-md hover:border-blue-300 hover:bg-blue-50/20 cursor-pointer transition-all h-10 group"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Hotel className="h-4 w-4 text-slate-400 flex-shrink-0 group-hover:text-blue-600" />
                  <span className="text-sm truncate text-slate-700">
                    {getRoomMixSummary()}
                  </span>
                </div>
                <span className="text-xs text-slate-400 group-hover:text-blue-600 flex-shrink-0 ml-1">
                  Edit
                </span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1">
            <div className="space-y-2">
              <Label>Purchase Price</Label>
              <Input
                type="number"
                placeholder="e.g., 2500000"
                value={formData.purchasePrice || ""}
                onChange={(e) =>
                  handleInputChange("purchasePrice", Number(e.target.value))
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Facilities</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-3">
            {facilities.map((facility) => (
              <label
                key={facility}
                className="flex items-center gap-2 cursor-pointer group"
              >
                <Checkbox
                  checked={(formData.facilities || []).includes(facility)}
                  onCheckedChange={() => handleFacilityToggle(facility)}
                  className="
                    data-[state=checked]:bg-[hsl(215,85%,45%)]
                    data-[state=checked]:border-[hsl(215,85%,45%)]
                    data-[state=checked]:text-white
                  "
                />
                <span className="text-sm capitalize">{facility}</span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Content</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              placeholder="Describe the property..."
              className="min-h-[80px]"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Investment Thesis</Label>
            <Textarea
              placeholder="Explain the investment opportunity..."
              className="min-h-[80px]"
              value={formData.investmentThesis}
              onChange={(e) =>
                handleInputChange("investmentThesis", e.target.value)
              }
            />
          </div>

          <div className="space-y-2">
            <Label>Images (up to 10)</Label>

            <input
              type="file"
              ref={fileInputRef}
              multiple
              accept="image/*"
              onChange={(e) => handleFileSelect(e.target.files)}
              className="hidden"
              disabled={uploading}
            />

            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                isDragging
                  ? "border-primary bg-primary/10"
                  : "border-gray-300 hover:border-gray-400"
              } ${uploading ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={uploading ? undefined : handleSelectImages}
              onDragOver={uploading ? undefined : handleDragOver}
              onDragLeave={uploading ? undefined : handleDragLeave}
              onDrop={uploading ? undefined : handleDrop}
            >
              <div className="space-y-3">
                <div className="flex justify-center">
                  <Upload className="h-10 w-10 text-gray-400" />
                </div>
                <div className="text-gray-600">
                  <p className="font-medium">
                    {uploading ? "Uploading..." : "Drag & drop images here"}
                  </p>
                  <p className="text-sm mt-1">
                    {uploading ? "Please wait" : "or click to browse files"}
                  </p>
                </div>
                <div className="text-xs text-gray-500">
                  Supports JPG, PNG, GIF, WebP • Max 10 images
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectImages();
                  }}
                  disabled={uploading}
                >
                  {uploading ? "Uploading..." : "Select Images"}
                </Button>
              </div>
            </div>

            {imageFiles.length > 0 && (
              <div className="text-sm text-gray-500 mt-2">
                {imageFiles.length}/10 images selected
                {mainImage && " • Main image selected"}
              </div>
            )}

            {imageFiles.length > 0 && (
              <div className="mt-4 space-y-4">
                {mainImage && (
                  <div>
                    <Label className="text-sm font-medium text-blue-600 flex items-center gap-1 mb-2">
                      <Star className="h-4 w-4 fill-current" /> Main Image
                    </Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      <div key={mainImage.id} className="relative group">
                        <div className="aspect-square rounded-lg overflow-hidden border-2 border-blue-500 bg-gray-100">
                          <img
                            src={mainImage.url}
                            alt="Main preview"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src =
                                "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNFNUU1RTUiLz48cGF0aCBkPSJNMTIwIDEwMEwxMDAgODAgODAgMTAwbDIwIDIwIDIwLTIwIDIwIDIwIDIwLTIwTDEyMCAxMDBaTTEwMCAxNDBBNDAgNDAgMCAxIDAgMTAwIDYwYTQwIDQwIDAgMCAwIDAgODBaIiBmaWxsPSIjQzVDNUM1Ii8+PC9zdmc+";
                            }}
                          />
                          <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                            Main
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage(mainImage.id);
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                          aria-label="Remove image"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {otherImages.length > 0 && (
                  <div>
                    <Label className="text-sm font-medium text-slate-600 flex items-center gap-1 mb-2">
                      <ImageIcon className="h-4 w-4" /> Additional Images (
                      {otherImages.length})
                    </Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {otherImages.map((image) => (
                        <div key={image.id} className="relative group">
                          <div className="aspect-square rounded-lg overflow-hidden border bg-gray-100">
                            <img
                              src={image.url}
                              alt="Preview"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.src =
                                  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNFNUU1RTUiLz48cGF0aCBkPSJNMTIwIDEwMEwxMDAgODAgODAgMTAwbDIwIDIwIDIwLTIwIDIwIDIwIDIwLTIwTDEyMCAxMDBaTTEwMCAxNDBBNDAgNDAgMCAxIDAgMTAwIDYwYTQwIDQwIDAgMCAwIDAgODBaIiBmaWxsPSIjQzVDNUM1Ii8+PC9zdmc+";
                              }}
                            />
                          </div>
                          <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              className="w-full text-xs h-7"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetMainImage(image.id);
                              }}
                            >
                              <Star className="h-3 w-3 mr-1" />
                              Set as Main
                            </Button>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveImage(image.id);
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                            aria-label="Remove image"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}

                      {imageFiles.length < 10 && (
                        <div
                          className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors"
                          onClick={handleSelectImages}
                        >
                          <Upload className="h-8 w-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-600">
                            Add more
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 p-4 border rounded-lg">
        <Checkbox
          id="featured"
          checked={formData.featured}
          onCheckedChange={(checked) => handleInputChange("featured", checked)}
        />
        <Label htmlFor="featured" className="cursor-pointer font-medium">
          Mark as featured deal template
        </Label>
      </div>

      <RoomMixModalAdmin
        dealId={dealId}
        isOpen={isRoomMixModalOpen}
        onClose={() => setIsRoomMixModalOpen(false)}
        onSaved={handleRoomMixSaved}
        roomMix={kpis?.roomMix || []}
        property={{
          ...formData,
          kpis: kpis,
        }}
      />
    </TabsContent>
  );
}
